// src/pages/markets/board/card/CardRow.tsx
//
// The row's surface belongs to the expand toggle now; the image is the only
// thing in it that navigates. Everything here stays presentational — the
// click handlers come from EventCard.

import { numSx } from '../../../../components/common/page/numberStyles';
import { Box, IconButton, Typography } from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { BoardScore, ScreenerEvent } from '../api/boardTypes';
import type { CardVariant } from '../board/columnConfig';
import { orderTags, tagColor } from '../board/columnConfig';
import { leadScore, netCapital, netSide, positionLabel } from '../api/netCapital';
import {
    activityRowMetric,
    directionColor,
    formatActivitySigned,
    type ActivityContext,
} from './ActivityBlock';
import { CARD_TEST_IDS } from './cardLayout';
import { EventImageLink } from './EventImageLink';
import { EventOpenLink } from './EventOpenLink';
import { useEventOutcomes } from '../api/useEventOutcomes';
import {
    needsTrueOutcomes,
    sideLabels,
    type SideLabels,
} from '../utils/boardOutcomeLabels';
import { sideColor } from '../utils/sideColor';
import { readableTone } from '../utils/readableTone';
import { OUTCOME_MAXLEN_BADGE } from '../../utils/outcomeLabels';
import {
    EM_DASH,
    ageTier,
    formatAge,
    formatCreatedDate,
    formatTimeLeft,
    formatUsd,
} from './cardFormat';

const IMAGE_SIZE = 28;
const MAX_TAGS = 2;

// Fixed, not content-width: a right-aligned box of varying content puts
// every row's digits in a different place, and the column stops scanning.
const VALUE_COL = 104;

export function TagChip({ text, strong }: { text: string; strong?: boolean }) {
    const theme = useTheme();
    const c = readableTone(tagColor(text), theme);
    return (
        <Box
            sx={{
                px: 0.5,
                borderRadius: '3px',
                border: '1px solid',
                borderColor: strong ? alpha(c, 0.7) : alpha(theme.palette.text.primary, 0.18),
                bgcolor: strong ? alpha(c, 0.16) : 'transparent',
                flexShrink: 0,
            }}
        >
            <Typography
                sx={{
                    fontSize: '10px',
                    fontWeight: strong ? 700 : 500,
                    color: strong ? c : theme.palette.text.disabled,
                    whiteSpace: 'nowrap',
                    lineHeight: 1.5,
                }}
            >
                {text}
            </Typography>
        </Box>
    );
}

interface RowValue {
    text: string;
    color: string;
    caption?: string;
}

// One number per column, in the same language the expanded card speaks:
// trader groups → net capital and the side it sits on, new → age,
// active → the sharpest move in the active unit.
function rowValue(
    variant: CardVariant,
    event: ScreenerEvent,
    score: BoardScore | null,
    now: number,
    theme: Theme,
    labels: SideLabels,
    activityCtx?: ActivityContext | null,
): RowValue {
    const { text } = theme.palette;

    if (variant === 'meta') {
        const tier = ageTier(event.created_at, now);
        return {
            text: formatAge(event.created_at, now),
            color:
                tier === 'hot'
                    ? theme.palette.warning.main
                    : tier === 'warm'
                        ? alpha(theme.palette.warning.main, 0.75)
                        : tier === 'today'
                            ? text.primary
                            : text.secondary,
            // The caption disambiguates the number above it. Volume did not.
            caption: formatCreatedDate(event.created_at, now),
        };
    }

    if (variant === 'activity') {
        if (!activityCtx || !activityCtx.activity) {
            return { text: EM_DASH, color: text.disabled, caption: 'no activity' };
        }
        const metric = activityRowMetric(activityCtx);
        return {
            text: formatActivitySigned(metric.value, activityCtx.metricMode, activityCtx.unit),
            color: activityCtx.activity.resolved
                ? theme.palette.warning.main
                : directionColor(metric.value, theme),
            caption: metric.caption,
        };
    }

    if (score) {
        return {
            text: formatUsd(Math.abs(netCapital(score))),
            color: sideColor(labels, netSide(score), theme),
            caption: positionLabel(event, score, labels),
        };
    }

    return { text: formatUsd(event.volume), color: text.disabled };
}

function RowValueBlock({ value, caption }: { value: RowValue; caption: string }) {
    return (
        <Box
            sx={{
                width: VALUE_COL,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
            }}
        >
            <Typography
                sx={{
                    maxWidth: '100%',
                    fontSize: '13px',
                    fontWeight: 700,
                    lineHeight: '18px',
                    color: value.color,
                    ...numSx,
                    whiteSpace: 'nowrap',
                }}
            >
                {value.text}
            </Typography>
            <Typography
                title={caption}
                sx={{
                    width: '100%',
                    textAlign: 'right',
                    fontSize: '10px',
                    lineHeight: '14px',
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {caption}
            </Typography>
        </Box>
    );
}

export interface CardRowProps {
    event: ScreenerEvent;
    score: BoardScore | null;
    // All scores of the active group — the row resolves its own market from these.
    scores?: BoardScore[];
    variant: CardVariant;
    now: number;
    activityCtx?: ActivityContext | null;
    onToggle?: (e: React.MouseEvent) => void;
    // Left click on the image; the anchor's href covers the modified clicks.
    onOpenEvent?: (e: React.MouseEvent) => void;
    activeTags?: readonly string[] | null;
}

export function CardRow({
    event,
    score,
    scores = [],
    variant,
    now,
    activityCtx,
    onToggle,
    onOpenEvent,
    activeTags,
}: CardRowProps) {
    const theme = useTheme();

    // Collapsed rows render instead of the card body, so they fetch the pair
    // themselves. Same gate as the body, and the query key is shared, so a card
    // that has been expanded once costs nothing when it collapses again.
    const needsOutcomes = event.markets.some(needsTrueOutcomes);
    const outcomesByMarket = useEventOutcomes(event.event_id, needsOutcomes);
    const isFlow = variant === 'capital' || variant === 'skill';
    const rowScore = isFlow ? leadScore(scores) ?? score : score;
    const labels = sideLabels(rowScore, OUTCOME_MAXLEN_BADGE, {
        outcomes: rowScore ? outcomesByMarket.get(rowScore.market_id) : null,
    });

    const ordered = orderTags(event.tags, activeTags);
    const strongCount = ordered.filter(t => t.strong).length;
    // Never truncate a highlighted tag — the grey tail is what gets cut.
    const shown = ordered.slice(0, Math.max(strongCount, MAX_TAGS));
    const extraTags = ordered.length - shown.length;

    const value = rowValue(variant, event, rowScore, now, theme, labels, activityCtx);
    const fallbackCaption = formatTimeLeft(event.end_date, now);

    return (
        <Box
            data-testid={CARD_TEST_IDS.row}
            sx={{
                height: '100%',
                px: 1,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                overflow: 'hidden',
            }}
        >
            <EventImageLink
                event={event}
                size={IMAGE_SIZE}
                radius="4px"
                fallbackFontSize="11px"
                onOpenEvent={onOpenEvent}
            />

            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    <Typography
                        sx={{
                            minWidth: 0,
                            fontSize: '12px',
                            fontWeight: 600,
                            lineHeight: 1.2,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {event.title ?? event.markets[0]?.question ?? event.event_id}
                    </Typography>
                    <EventOpenLink event={event} onOpenEvent={onOpenEvent} size={12} />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, overflow: 'hidden' }}>
                    {shown.map(t => (
                        <TagChip key={t.text} text={t.text} strong={t.strong} />
                    ))}
                    {extraTags > 0 && (
                        <Typography sx={{ fontSize: '10px', color: 'text.disabled', whiteSpace: 'nowrap' }}>
                            +{extraTags}
                        </Typography>
                    )}
                </Box>
            </Box>

            <RowValueBlock value={value} caption={value.caption ?? fallbackCaption} />

            {onToggle && (
                <IconButton
                    size="small"
                    onClick={onToggle}
                    aria-label="Expand to card"
                    sx={{ p: 0.25, flexShrink: 0 }}
                >
                    <ExpandMoreIcon sx={{ fontSize: 16 }} />
                </IconButton>
            )}
        </Box>
    );
}